import { useState, useEffect } from 'react'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { resolveMcpBaseUrl } from '../lib/mcpConfig'

export function Admin() {
  const [githubOrg, setGithubOrg] = useState('')
  const [tokenIsSet, setTokenIsSet] = useState(false)
  const [repoCount, setRepoCount] = useState<number | null>(null)
  const [testResult, setTestResult] = useState<{ ok: boolean; count?: number; error?: string } | null>(null)

  useEffect(() => {
    // Check connection status on load
    testConnection()
  }, [])

  async function testConnection() {
    setTestResult(null)
    try {
      const res = await fetch('/api/v1/sources/repos')
      const data = await res.json()
      if (data.error) {
        setTestResult({ ok: false, error: data.error })
      } else {
        setTestResult({ ok: true, count: data.repos?.length ?? 0 })
        setGithubOrg(data.org || '')
        setTokenIsSet(true)
        setRepoCount(data.repos?.length ?? 0)
      }
    } catch { setTestResult({ ok: false, error: 'Connection failed' }) }
  }

  return (
    <div style={{ padding: 'var(--space-4)', maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 'var(--space-4)' }}>Platform Admin</h1>

      <Card header="GitHub Connection" style={{ marginBottom: 'var(--space-3)' }}>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
          GitHub connection is configured via environment variables (Secrets Manager). All changes go through Git.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>GitHub Org</span>
            <code style={{ fontSize: 13 }}>{githubOrg || '—'}</code>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Token</span>
            {tokenIsSet ? <Badge variant="success">configured</Badge> : <Badge variant="warning">not set</Badge>}
          </div>
          {repoCount !== null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Repositories visible</span>
              <span style={{ fontSize: 13 }}>{repoCount}</span>
            </div>
          )}
          {testResult && !testResult.ok && (
            <p style={{ fontSize: 12, color: 'var(--error)', marginTop: 4 }}>
              {testResult.error}
            </p>
          )}
        </div>
      </Card>

      <Card header="MCP Server" style={{ marginBottom: 'var(--space-3)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Endpoint</span>
            <code style={{ fontSize: 13 }}>{resolveMcpBaseUrl()}</code>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Status</span>
            <Badge variant="success">Connected</Badge>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Platform API</span>
            <code style={{ fontSize: 13 }}>https://sdlc-api.dev.wenni.io</code>
          </div>
        </div>
      </Card>

      <Card header="Canonical Store" style={{ marginBottom: 'var(--space-3)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Source</span>
            <code style={{ fontSize: 13 }}>sdlc-canonical/</code>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Sync</span>
            <Badge variant="success">Up to date</Badge>
          </div>
        </div>
      </Card>

      <Card header="Integration">
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
          Each project only needs two files to connect:
        </p>
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          <li><code>.mcp.json</code> — points the IDE to the MCP server</li>
          <li><code>.sdlc/config.yaml</code> — declares team, project, and profiles</li>
        </ol>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
          IDE wiring files (.github/agents/, .cursor/rules/, etc.) are generated automatically on first session.
        </p>
      </Card>
    </div>
  )
}
